import React, { Dispatch, SetStateAction } from 'react';

import { Button, Divider, Group, Skeleton, Stack, Text, Title } from '@mantine/core';
import Drawer from 'components/Drawer';
import dayjs from 'dayjs';
import useCommunication from 'hooks/communications/useCommunication';

interface IDrawerCommunicationProps {
  opened: boolean;
  closeHandler: () => void;
  communicationsId: string | null;
  setCommunicationsId: Dispatch<SetStateAction<string | null>>;
}

const DrawerCommunication = ({
  opened,
  closeHandler,
  communicationsId,
  setCommunicationsId,
}: IDrawerCommunicationProps): JSX.Element => {
  const { data, isLoading } = useCommunication(communicationsId || '');

  const handleCloseAndReset = (): void => {
    setCommunicationsId(null);
    closeHandler();
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      size='lg'
      title={<Title order={3}>View Communication</Title>}
      footer={
        <Group position='right' align='center'>
          <Button variant='outline' onClick={handleCloseAndReset}>
            Cancel
          </Button>
        </Group>
      }
    >
      <Stack mih={'100%'} justify='space-between'>
        <Skeleton visible={isLoading}>
          <Stack>
            <Title>{data?.data.contentPreview}</Title>
            <Text dangerouslySetInnerHTML={{ __html: data?.data.contentBody || '' }} />
          </Stack>
        </Skeleton>
        <Skeleton visible={isLoading}>
          <Stack>
            <Divider />
            <Group>
              <Text>Sent:</Text>
              <Text color='dimmed'>{dayjs(data?.data.createdAt).format('MM/DD/YYYY hh:mm A')}</Text>
            </Group>
            <Group>
              <Text>Type:</Text>
              <Text color='dimmed'>{data?.data.notificationType}</Text>
            </Group>
            {data?.data.deliveryStatus && (
              <Group>
                <Text>Status:</Text>
                <Text color='dimmed'>{data?.data.deliveryStatus}</Text>
              </Group>
            )}
            {/* Only show the status time when it differs from Sent (most deliveries land the same minute). */}
            {data?.data.deliveryStatusOccurredAt &&
              !dayjs(data?.data.deliveryStatusOccurredAt).isSame(dayjs(data?.data.createdAt), 'minute') && (
                <Group>
                  <Text>Status updated:</Text>
                  <Text color='dimmed'>
                    {dayjs(data?.data.deliveryStatusOccurredAt).format('MM/DD/YYYY hh:mm A')}
                  </Text>
                </Group>
              )}
            {data?.data.deliveryStatusReason && (
              <Group>
                <Text>Reason:</Text>
                <Text color='dimmed'>{data?.data.deliveryStatusReason}</Text>
              </Group>
            )}
          </Stack>
        </Skeleton>
      </Stack>
    </Drawer>
  );
};

export default DrawerCommunication;
